/**
 * 中国法定节假日 / 调休补班（与 backend/utils/legalHolidayCalendar.js 保持一致）
 * 用于管理端日历展示、周餐推算、休息日判断。
 */

const HOLIDAYS_2025 = [
  '2025-01-01',
  '2025-01-28', '2025-01-29', '2025-01-30', '2025-01-31',
  '2025-02-01', '2025-02-02', '2025-02-03', '2025-02-04',
  '2025-04-04', '2025-04-05', '2025-04-06',
  '2025-05-01', '2025-05-02', '2025-05-03', '2025-05-04', '2025-05-05',
  '2025-05-31', '2025-06-01', '2025-06-02',
  '2025-10-01', '2025-10-02', '2025-10-03', '2025-10-04',
  '2025-10-05', '2025-10-06', '2025-10-07', '2025-10-08'
]

const MAKEUP_WORKDAYS_2025 = [
  '2025-01-26',
  '2025-02-08',
  '2025-04-27',
  '2025-09-28',
  '2025-10-11'
]

const HOLIDAYS_2026 = [
  '2026-01-01', '2026-01-02', '2026-01-03',
  '2026-02-15', '2026-02-16', '2026-02-17', '2026-02-18', '2026-02-19',
  '2026-02-20', '2026-02-21', '2026-02-22', '2026-02-23',
  '2026-04-04', '2026-04-05', '2026-04-06',
  '2026-05-01', '2026-05-02', '2026-05-03', '2026-05-04', '2026-05-05',
  '2026-06-19', '2026-06-20', '2026-06-21',
  '2026-09-25', '2026-09-26', '2026-09-27',
  '2026-10-01', '2026-10-02', '2026-10-03', '2026-10-04',
  '2026-10-05', '2026-10-06', '2026-10-07'
]

/** 调休补班：周末需上班，计入排餐 / 出勤 */
const MAKEUP_WORKDAYS_2026 = [
  '2026-02-14',
  '2026-02-28',
  '2026-05-09',
  '2026-09-20',
  '2026-10-10'
]

const HOLIDAY_SET = new Set([...HOLIDAYS_2025, ...HOLIDAYS_2026])
const MAKEUP_SET = new Set([...MAKEUP_WORKDAYS_2025, ...MAKEUP_WORKDAYS_2026])

const HOLIDAY_NAME = {
  '2026-01-01': '元旦',
  '2026-01-02': '元旦',
  '2026-01-03': '元旦',
  '2026-02-14': '春节调休补班',
  '2026-02-15': '春节',
  '2026-02-16': '春节',
  '2026-02-17': '春节',
  '2026-02-18': '春节',
  '2026-02-19': '春节',
  '2026-02-20': '春节',
  '2026-02-21': '春节',
  '2026-02-22': '春节',
  '2026-02-23': '春节',
  '2026-02-28': '春节调休补班',
  '2026-04-04': '清明',
  '2026-04-05': '清明',
  '2026-04-06': '清明',
  '2026-05-01': '劳动节',
  '2026-05-02': '劳动节',
  '2026-05-03': '劳动节',
  '2026-05-04': '劳动节',
  '2026-05-05': '劳动节',
  '2026-05-09': '劳动节调休补班',
  '2026-06-19': '端午',
  '2026-06-20': '端午',
  '2026-06-21': '端午',
  '2026-09-20': '中秋国庆调休补班',
  '2026-09-25': '中秋',
  '2026-09-26': '中秋',
  '2026-09-27': '中秋',
  '2026-10-01': '国庆',
  '2026-10-02': '国庆',
  '2026-10-03': '国庆',
  '2026-10-04': '国庆',
  '2026-10-05': '国庆',
  '2026-10-06': '国庆',
  '2026-10-07': '国庆',
  '2026-10-10': '国庆调休补班'
}

function formatDate(d) {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function toDateObj(d) {
  if (d instanceof Date) return d
  const s = formatDate(d)
  return new Date(`${s}T12:00:00`)
}

function isWeekend(d) {
  const w = toDateObj(d).getDay()
  return w === 0 || w === 6
}

function isHoliday(d) {
  return HOLIDAY_SET.has(formatDate(d))
}

function isMakeupWorkday(d) {
  return MAKEUP_SET.has(formatDate(d))
}

function getHolidayName(d) {
  return HOLIDAY_NAME[formatDate(d)] || ''
}

/**
 * 是否休息日：补班优先上班；法定假休息；否则周末休息
 */
function isRestDay(d) {
  const key = formatDate(d)
  if (MAKEUP_SET.has(key)) return false
  if (HOLIDAY_SET.has(key)) return true
  return isWeekend(d)
}

function isWorkday(d) {
  return !isRestDay(d)
}

/**
 * 根据下单时间计算首个配送日
 * 规则：工作日下单次日配送；周五/周六/周日下单均为下周一起送；遇休息日顺延至最近工作日
 */
function getFirstDeliveryDate(orderDate) {
  const base = orderDate instanceof Date ? new Date(orderDate) : new Date(orderDate)
  let candidate = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1)
  const w = base.getDay()
  if (w === 5) {
    candidate = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 3)
  } else if (w === 6) {
    candidate = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 2)
  } else if (w === 0) {
    candidate = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1)
  }
  while (isRestDay(candidate)) {
    candidate = new Date(candidate.getFullYear(), candidate.getMonth(), candidate.getDate() + 1)
  }
  return candidate
}

/**
 * 生成某月日历数据：休 / 班 标注
 */
function buildSubscriptionCalendar(year, monthIndex) {
  const days = []
  const firstDay = new Date(year, monthIndex, 1)
  const nextMonthFirst = new Date(year, monthIndex + 1, 1)
  const totalDays = Math.round((nextMonthFirst - firstDay) / (1000 * 60 * 60 * 24))
  for (let i = 1; i <= totalDays; i++) {
    const d = new Date(year, monthIndex, i)
    const dateStr = formatDate(d)
    const makeup = isMakeupWorkday(d)
    const holiday = isHoliday(d)
    const rest = isRestDay(d)
    let label = ''
    if (makeup) label = '班'
    else if (rest) label = '休'
    days.push({
      date: d,
      dateStr,
      day: i,
      isRest: rest,
      isHoliday: holiday,
      isMakeup: makeup,
      name: getHolidayName(d),
      label
    })
  }
  return days
}

/**
 * 从起始日起连续计 n 个工作日（含起始日若为工作日），返回该段最后一个工作日。
 * 用于「周餐」等：选择起始日期后自动推算周期结束（跳过周末、节假日；计入补班）。
 */
function businessDaysInclusiveEnd(startInput, businessDayCount) {
  const n = Math.max(1, Math.floor(Number(businessDayCount) || 1))
  let d = startInput instanceof Date ? new Date(startInput) : new Date(startInput)
  if (Number.isNaN(d.getTime())) return null
  while (isRestDay(d)) {
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
  }
  let end = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  let left = n
  while (left > 1) {
    end = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
    while (isRestDay(end)) {
      end = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
    }
    left -= 1
  }
  return end
}

export {
  HOLIDAYS_2026,
  MAKEUP_WORKDAYS_2026,
  HOLIDAYS_2025,
  MAKEUP_WORKDAYS_2025,
  formatDate,
  isWeekend,
  isHoliday,
  isMakeupWorkday,
  getHolidayName,
  isRestDay,
  isWorkday,
  getFirstDeliveryDate,
  buildSubscriptionCalendar,
  businessDaysInclusiveEnd
}
